/**
 * Человек поменял сумму на вилке — дописать её в уже открытые купоны.
 *
 * Это не обещание и не новая попытка: сумму, шейры, время и кэф в ключ
 * попытки класть нельзя, иначе живой тик стирает поле и снова жмёт исход.
 * Конверт уходит только после ввода на вилке, обе ноги одним снимком.
 */

import { intentOf, withoutIntent } from './intent';
import { marketExtras } from './expect';
import { sameBook } from './pages';

export const STAKE_PUSH = 'forq:stake-push';

export type StakePushLeg = {
  venue: string;
  url: string;
  intent?: string;
  amount?: string;
  currency?: string;
  shares?: string;
  limitPrice?: string;
  cents?: string;
  order?: 'market';
  marketUsd?: string;
  dead?: string;
  fillStake?: false;
  prepareOnly?: true;
};

export type StakePush = {
  channel: typeof STAKE_PUSH;
  forkId: string;
  rev: number;
  at: number;
  legs: StakePushLeg[];
};

const AMOUNT = /^\d{1,12}(\.\d{1,8})?$/;
const SHARES = /^\d+(\.\d{1,4})?$/;
const VENUE = /^[a-z][a-z0-9_]{0,31}$/;
const INTENT = /^[a-zA-Z0-9_-]{16,80}$/;
const HTTP = /^https?:\/\//i;
const MAX_LEGS = 4;

function parseLeg(raw: unknown): StakePushLeg | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const one = raw as Record<string, unknown>;
  if (typeof one.venue !== 'string' || !VENUE.test(one.venue)) return null;
  if (typeof one.url !== 'string' || !HTTP.test(one.url) || one.url.length > 2048) return null;
  const leg: StakePushLeg = { venue: one.venue, url: withoutIntent(one.url) };
  if (typeof one.intent === 'string' && INTENT.test(one.intent)) leg.intent = one.intent;
  else {
    const marked = intentOf(one.url);
    if (marked) leg.intent = marked;
  }
  if (typeof one.amount === 'string' && AMOUNT.test(one.amount) && one.amount !== '0') {
    leg.amount = one.amount;
  }
  if (typeof one.currency === 'string' && /^[A-Z]{3,6}$/.test(one.currency)) {
    leg.currency = one.currency;
  }
  if (typeof one.shares === 'string' && SHARES.test(one.shares) && one.shares !== '0') {
    leg.shares = one.shares;
  }
  if (typeof one.limitPrice === 'string' && /^0\.\d{1,6}$/.test(one.limitPrice)
    && /[1-9]/.test(one.limitPrice)) {
    leg.limitPrice = one.limitPrice;
  }
  if (typeof one.cents === 'string' && /^\d+$/.test(one.cents) && one.cents !== '0') {
    leg.cents = one.cents;
  }
  Object.assign(leg, marketExtras(one));
  if (one.fillStake === false) leg.fillStake = false;
  if (one.prepareOnly === true) leg.prepareOnly = true;
  if (leg.amount === undefined && leg.shares === undefined) return null;
  return leg;
}

export function stakePushEnvelope(
  forkId: string,
  rev: number,
  at: number,
  legs: readonly StakePushLeg[],
): StakePush {
  return {
    channel: STAKE_PUSH,
    forkId: forkId.slice(0, 200),
    rev: Number.isFinite(rev) ? Math.max(1, Math.floor(rev)) : 1,
    at: Number.isFinite(at) ? at : 0,
    legs: legs.filter((leg) => parseLeg(leg) !== null).slice(0, MAX_LEGS),
  };
}

export function parseStakePush(data: unknown): StakePush | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== STAKE_PUSH) return null;
  if (typeof body.forkId !== 'string' || body.forkId === '' || body.forkId.length > 200) return null;
  if (typeof body.rev !== 'number' || !Number.isFinite(body.rev) || body.rev < 1) return null;
  if (typeof body.at !== 'number' || !Number.isFinite(body.at)) return null;
  if (!Array.isArray(body.legs)) return null;
  const legs: StakePushLeg[] = [];
  for (const raw of body.legs.slice(0, MAX_LEGS)) {
    const one = parseLeg(raw);
    if (one !== null) legs.push(one);
  }
  if (legs.length === 0) return null;
  return {
    channel: STAKE_PUSH,
    forkId: body.forkId,
    rev: Math.floor(body.rev),
    at: body.at,
    legs,
  };
}

/** Что печатать в уже найденное поле: на бирже шейры, иначе сумма. */
export function valueToFill(leg: StakePushLeg): string | null {
  if (typeof leg.shares === 'string' && leg.shares !== '') return leg.shares;
  if (typeof leg.amount === 'string' && leg.amount !== '') return leg.amount;
  return null;
}

export function acceptsPushLeg(
  page: { venue: string; url: string; intent?: string },
  leg: StakePushLeg,
): boolean {
  if (page.venue !== leg.venue) return false;
  if (leg.fillStake === false || leg.prepareOnly === true) return false;
  const pageIntent = page.intent ?? intentOf(page.url);
  const legIntent = leg.intent ?? intentOf(leg.url);
  if (pageIntent === '' || legIntent === '') return false;
  if (pageIntent && legIntent && pageIntent !== legIntent) return false;
  return sameBook(page.url, leg.url);
}

export function pickPushLeg(
  push: StakePush,
  page: { venue: string; url: string; intent?: string },
): StakePushLeg | null {
  return push.legs.find((leg) => acceptsPushLeg(page, leg)) ?? null;
}
